import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { RegistryTableComponent } from '../../../core/registry-table/registry-table.component';
import { ConfirmationDialogService } from '../../services/confirmation-dialog.service';
import { ColumnConfig } from '../dashboard/table-config';
import { GeographyService, Province, Region } from '../../../core/services/geography.service';
import { FormDropdownService } from '../../../core/services/form-dropdown.service';
import { ProfessionalsService } from '../../../core/services/professionals.service';
import {
  MfaAssociation,
  OperationalSite,
  SOL_HIERARCHY_SUFFIXES,
  SolCode,
  SolCodeMfa,
  SolCodeService,
  SolCodeStatus,
  SolCodeUpsert,
  SolProfessionalType,
} from '../../services/sol-code.service';

interface SolForm {
  code: string;
  /** Denominazione del codice SOL: precompilata dai dati anagrafici, editabile. */
  denomination: string;
  category: string;
  /** True quando il codice è il master del fiduciario: la gerarchia coincide col codice ed è bloccata. */
  isMaster: boolean;
  /** True quando la "Gerarchia – Codice SOL" coincide con il codice stesso. */
  hierarchySameAsCode: boolean;
  hierarchyParentCode: string;
  hierarchySuffix: string | null;
  description: string;
  /** True se il SOL è su sede fisica; false se su territorio (Regione/Provincia obbligatorie). */
  physicalSite: boolean;
  region: string;
  province: string;
}

/** Riga della tabella A: un codice SOL esistente oppure una sede ancora priva di codici (da completare). */
interface SolRow {
  kind: 'sol' | 'pending';
  siteId: number;
  siteAddress: string;
  legalFallback: boolean;
  sol: SolCode | null;
}

/** Riga piatta per la registry-table A: campi display già pronti + riferimenti per le azioni. */
interface SolRowView extends SolRow {
  codeDisplay: string;
  /** Denominazione del codice SOL (colonna dedicata). */
  denominationDisplay: string;
  categoryDisplay: string;
  /**
   * Stato grezzo del SOL per il tag ('' per le righe "da inserire"). Field dedicato
   * ('solStatus', non 'status') così la registry-table applica label/severity specifici
   * del ciclo di vita SOL senza collidere con i generici ACTIVE/INACTIVE/SUSPENDED.
   */
  solStatus: string;
  hierarchyDisplay: string;
  /** Riferimento sede fisica o territorio (Regione/Provincia) del SOL. */
  locationDisplay: string;
}

/** Opzione della tendina "Sede operativa": etichetta = indirizzo decodificato. */
interface SiteOption {
  value: number;
  label: string;
}

/** Categoria (codice numerico) precompilata dal tipo fiduciario. Rispecchia la logica BE. */
const CATEGORY_BY_TYPE: Partial<Record<SolProfessionalType, string>> = {
  AUTO_EXPERT: '1',
  RE_EXPERT: '2',
  INVESTIGATOR: '3',
  DOCTOR: '4',
  LAWYER: '5',
};

@Component({
  selector: 'app-sol-codes-manager',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    TooltipModule,
    ToastModule,
    TagModule,
    CheckboxModule,
    Select,
    RegistryTableComponent,
  ],
  providers: [MessageService],
  templateUrl: './sol-codes-manager.component.html',
  styleUrls: ['./sol-codes-manager.component.scss'],
})
export class SolCodesManagerComponent {
  private solCodeService = inject(SolCodeService);
  private geographyService = inject(GeographyService);
  private formDropdownService = inject(FormDropdownService);
  private professionalsService = inject(ProfessionalsService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private messageService = inject(MessageService);

  // ── Inputs ─────────────────────────────────────────────────────────────────
  professionalId = input.required<string>();
  professionalType = input<string>('');
  canManage = input<boolean>(true);
  /**
   * True quando la sezione è in fase di validazione MFA (dati compilati dal fiduciario,
   * in attesa della revisione del Back Office). In questa fase la tabella è di sola
   * lettura (solo occhio) finché il BO non sblocca esplicitamente la modifica: ogni
   * modifica è irreversibile e genera una nuova richiesta di compilazione MFA.
   */
  mfaReviewMode = input<boolean>(false);
  /**
   * True quando il fiduciario è in fase contrattuale (contratto inviato/firma/controfirma):
   * i codici SOL sono già confermati ma non ancora attivi. In questa fase l'aggiunta di nuovi
   * codici SOL è bloccata; torna disponibile solo dopo l'attivazione del fiduciario.
   */
  contractPhase = input<boolean>(false);
  /** Dati MFA (mail principale/responsabile/associazioni) per il dettaglio dell'occhio. */
  mfaData = input<SolCodeMfa[]>([]);

  // ── Outputs ────────────────────────────────────────────────────────────────
  /** Emesso dopo una modifica strutturale (crea/modifica/elimina) andata a buon fine. */
  solCodesChanged = output<void>();
  /** Richiesta di export Excel MFA (delegata al contenitore che possiede l'endpoint). */
  exportMfa = output<void>();

  // ── State ──────────────────────────────────────────────────────────────────
  collapsed = signal<boolean>(false);
  /**
   * Modifica sbloccata dal BO in fase di validazione MFA: abilita Aggiungi/Modifica/Elimina.
   * Fuori dalla fase MFA la gestione è sempre libera (post-contrattuale ancora da definire).
   */
  editMode = signal<boolean>(false);
  sites = signal<OperationalSite[]>([]);
  solCodes = signal<SolCode[]>([]);
  loadingSol = signal<boolean>(false);

  // Geografia (SOL su territorio): Regione → Provincia in cascata, senza precompilazione.
  regions = signal<Region[]>([]);
  provinces = signal<Province[]>([]);
  loadingProvinces = signal<boolean>(false);

  // Decodifiche di dominio (codice → etichetta): il BE espone i codici grezzi.
  /** Tipo via/toponimo (dominio PLACE_NAME): codice → descrizione (es. "V" → "Via"). */
  streetTypeMap = signal<Map<string, string>>(new Map());
  /** Comune: codice → nome (es. "H501" → "Roma"). Popolato per le province delle sedi caricate. */
  municipalityMap = signal<Map<string, string>>(new Map());
  /** Tipo società (dominio LEGAL_PERSON): codice → descrizione (es. "DI" → "Ditta Individuale (D.I.)"). */
  companyTypeMap = signal<Map<string, string>>(new Map());
  /** Dati anagrafici per precompilare la denominazione del codice SOL. */
  companyData = signal<{
    businessName: string | null;
    firstname: string | null;
    lastname: string | null;
    legalOrPhysical: string | null;
    companyType: string | null;
  } | null>(null);

  // Dialog "Aggiungi / Modifica codice SOL"
  showFormDialog = signal<boolean>(false);
  editingId = signal<number | null>(null);
  // Sede a cui associare il codice in fase di salvataggio (null = sede singola risolta dal BE).
  targetSiteId = signal<number | null>(null);
  saving = signal<boolean>(false);
  form = signal<SolForm>(this.emptyForm());
  // Il flag "master" è auto-determinato (prima assegnazione della sede = master) e non modificabile.
  masterLocked = signal<boolean>(true);
  // True dopo il primo "Salva": abilita l'evidenziazione rossa dei campi obbligatori non compilati.
  formSubmitted = signal<boolean>(false);

  // Dialog dettaglio (occhio) tabella A
  showDetailDialog = signal<boolean>(false);
  selectedSol = signal<SolCode | null>(null);

  // ── Derived ────────────────────────────────────────────────────────────────
  /** Opzioni tendina Regione (nome leggibile, valore = nome per continuità di visualizzazione). */
  regionOptions = computed<{ label: string; value: string }[]>(() =>
    this.regions().map((r) => ({ label: r.name, value: r.name })),
  );

  /** Opzioni tendina Provincia, dipendenti dalla regione selezionata. */
  provinceOptions = computed<{ label: string; value: string }[]>(() =>
    this.provinces().map((p) => ({ label: p.name, value: p.name })),
  );

  /**
   * Opzioni tendina "Sede operativa": etichetta = indirizzo decodificato.
   * Le sedi che coincidono con la sede legale sono prefissate con "(SL) - " (Sede Legale) e
   * mostrate sempre in prima posizione; le altre seguono in ordine alfabetico per indirizzo.
   */
  siteOptions = computed<SiteOption[]>(() =>
    this.sites()
      .map((s) => ({ value: s.id, address: this.formatSiteAddress(s), legalFallback: s.legalFallback }))
      .sort((a, b) => {
        // Sede legale sempre in cima, poi ordine alfabetico per indirizzo.
        if (a.legalFallback !== b.legalFallback) {
          return a.legalFallback ? -1 : 1;
        }
        return a.address.localeCompare(b.address, 'it', { sensitivity: 'base' });
      })
      .map((o) => ({
        value: o.value,
        label: o.legalFallback ? `(SL) - ${o.address}` : o.address,
      })),
  );

  /** Id della sede legale (link figurativo di default per i SOL non su sede fisica); '' se assente. */
  private legalSiteId(): number | null {
    const sites = this.sites();
    return sites.find((s) => s.legalFallback)?.id ?? sites[0]?.id ?? null;
  }

  /**
   * Indirizzo leggibile della sede: tipo via decodificato + via/civico, CAP, comune (provincia).
   * Es. "Via Roma 12, 00100 Roma (RM)". Se la sede non ha indirizzo, ripiega su "Sede legale".
   */
  formatSiteAddress(site: OperationalSite): string {
    const streetTypeLabel = site.streetType
      ? this.streetTypeMap().get(site.streetType) ?? site.streetType
      : '';
    const street = [streetTypeLabel, site.streetName, site.streetNumber]
      .map((v) => v?.trim())
      .filter(Boolean)
      .join(' ');
    const municipalityCode = site.municipality?.trim();
    const municipalityLabel = municipalityCode
      ? this.municipalityMap().get(municipalityCode) ?? municipalityCode
      : '';
    const locality = [site.zipCode?.trim(), municipalityLabel].filter(Boolean).join(' ');
    const withProvince = site.province?.trim() ? `${locality} (${site.province.trim()})` : locality;
    const full = [street, withProvince].filter(Boolean).join(', ');
    return full || (site.legalFallback ? 'Sede legale' : `Sede #${site.id}`);
  }

  /**
   * Denominazione precompilata per un nuovo codice SOL:
   * - con ragione sociale → ragione sociale + tipo società (decodificato);
   * - senza ragione sociale → cognome + nome del rappresentante legale.
   * Sempre editabile dal BO.
   */
  private suggestedDenomination(): string {
    const cd = this.companyData();
    if (!cd) {
      return '';
    }
    const businessName = cd.businessName?.trim();
    if (businessName) {
      const typeLabel = cd.companyType ? this.companyTypeMap().get(cd.companyType) ?? '' : '';
      return [businessName, typeLabel?.trim()].filter(Boolean).join(' ');
    }
    return [cd.lastname?.trim(), cd.firstname?.trim()].filter(Boolean).join(' ');
  }

  suffixOptions = computed<{ label: string; value: string }[]>(() => {
    const type = this.professionalType() as SolProfessionalType;
    const domain = SOL_HIERARCHY_SUFFIXES[type] ?? [];
    return domain.map((v) => ({ label: v, value: v }));
  });

  /** Il campo "Gerarchia – Suffisso" è mostrato solo per le tipologie con dominio. */
  showSuffix = computed<boolean>(() => this.suffixOptions().length > 0);

  dialogTitle = computed(() => (this.editingId() === null ? 'Aggiungi codice SOL' : 'Modifica codice SOL'));

  /** Testo esplicativo sotto il flag master (read-only, auto-determinato). */
  masterHint = computed<string>(() =>
    this.form().isMaster
      ? 'Ogni fiduciario deve avere un codice SOL master: questo sarà il master del fiduciario.'
      : 'Il fiduciario ha già un master: il codice sarà associato come non-master.',
  );

  // Errori lato server per singolo campo (es. codice già usato): evidenziano il campo in rosso.
  serverErrors = signal<{ code?: string }>({});

  // Validazione campi obbligatori (evidenziati in rosso dopo il Salva). Descrizione esclusa.
  // Il Codice combina l'obbligatorietà con l'eventuale messaggio di duplicato dal server.
  codeError = computed<string>(() => {
    if (this.serverErrors().code) return this.serverErrors().code!;
    if (this.formSubmitted() && !this.form().code?.trim()) return 'Campo obbligatorio';
    return '';
  });
  invalidDenomination = computed<boolean>(() => this.formSubmitted() && !this.form().denomination?.trim());
  invalidCategory = computed<boolean>(() => this.formSubmitted() && !this.form().category?.trim());
  invalidSuffix = computed<boolean>(() => this.formSubmitted() && this.showSuffix() && !this.form().hierarchySuffix);
  // Regione/Provincia obbligatorie solo per i SOL su territorio (physicalSite === false).
  invalidRegion = computed<boolean>(
    () => this.formSubmitted() && !this.form().physicalSite && !this.form().region?.trim(),
  );
  invalidProvince = computed<boolean>(
    () => this.formSubmitted() && !this.form().physicalSite && !this.form().province?.trim(),
  );
  // La sede associata è sempre obbligatoria: sede operativa per i SOL su sede fisica,
  // sede fisica associata (default = legale) per i SOL su territorio.
  invalidSite = computed<boolean>(() => this.formSubmitted() && this.targetSiteId() == null);

  private isFormValid(): boolean {
    const f = this.form();
    return (
      !!f.code?.trim() &&
      !!f.denomination?.trim() &&
      !!f.category?.trim() &&
      this.targetSiteId() != null &&
      (f.physicalSite || (!!f.region?.trim() && !!f.province?.trim())) &&
      (f.hierarchySameAsCode || !!f.hierarchyParentCode?.trim()) &&
      (!this.showSuffix() || !!f.hierarchySuffix)
    );
  }

  /** Etichetta leggibile dello stato del ciclo di vita del codice SOL. */
  statusLabel(status: SolCodeStatus): string {
    switch (status) {
      case 'TO_ACTIVATE':
        return 'Da attivare';
      case 'ACTIVE':
        return 'Attivo';
      case 'SUSPENDED':
        return 'Sospeso';
      case 'INACTIVE':
        return 'Disattivo';
      default:
        return status;
    }
  }

  /** Severity del tag di stato del codice SOL. */
  statusSeverity(status: SolCodeStatus): 'secondary' | 'success' | 'warn' | 'danger' {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'SUSPENDED':
        return 'warn';
      case 'INACTIVE':
        return 'danger';
      case 'TO_ACTIVATE':
      default:
        return 'secondary';
    }
  }

  /**
   * Righe tabella A (pivot v2): una riga per ogni codice SOL esistente (master + aggiuntivi).
   * Il master è l'unico codice obbligatorio: finché manca si mostra UNA sola riga "Da inserire"
   * (il master si crea scegliendo la sede nel dialog). I SOL aggiuntivi sono a discrezione del BO,
   * perciò le sedi prive di codici NON generano righe "da inserire".
   */
  rows = computed<SolRow[]>(() => {
    const siteById = new Map(this.sites().map((s) => [s.id, s]));
    const codes = this.solCodes();
    const result: SolRow[] = [];

    // Finché manca il master (unico obbligatorio): una sola riga "Da inserire" per crearlo.
    if (!codes.some((c) => c.master)) {
      result.push({
        kind: 'pending',
        siteId: 0,
        siteAddress: '',
        legalFallback: false,
        sol: null,
      });
    }

    // Ordinamento stabile e indipendente dall'ultima modifica: il master è sempre primo,
    // gli altri codici seguono in ordine alfanumerico (naturale) per codice SOL.
    const sortedCodes = [...codes].sort((a, b) => {
      if (a.master !== b.master) return a.master ? -1 : 1;
      return (a.code ?? '').localeCompare(b.code ?? '', undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    });

    // Una riga per ciascun codice SOL esistente.
    for (const sol of sortedCodes) {
      const site = siteById.get(sol.siteId);
      result.push({
        kind: 'sol',
        siteId: sol.siteId,
        siteAddress: sol.siteAddress || site?.address || '',
        legalFallback: site?.legalFallback ?? false,
        sol,
      });
    }

    return result;
  });

  /** True se il fiduciario ha già il codice SOL master (unico obbligatorio, pivot v2). */
  hasMaster = computed<boolean>(() => this.solCodes().some((c) => c.master));

  /**
   * Unico vincolo di completezza (pivot v2): il fiduciario deve avere un codice SOL master.
   * Le sedi senza SOL sono ammesse (i SOL aggiuntivi sono opzionali, a discrezione del BO).
   */
  missingMaster = computed<boolean>(() => !this.hasMaster());

  /** Colonne della registry-table A (Fiduciario e sedi). */
  tableAColumns: ColumnConfig[] = [
    { title: 'Codice SOL', field: 'codeDisplay', filterType: 'text', filterPlaceholder: 'Cerca codice' },
    { title: 'Denominazione', field: 'denominationDisplay', filterType: 'text', filterPlaceholder: 'Cerca denominazione' },
    { title: 'Categoria', field: 'categoryDisplay', filterType: 'none' },
    {
      title: 'Stato',
      field: 'solStatus',
      type: 'tag',
      filterType: 'select',
      filterPlaceholder: 'Filtra stato',
      filterOptions: [
        { label: 'Da attivare', value: 'TO_ACTIVATE' },
        { label: 'Attivo', value: 'ACTIVE' },
        { label: 'Sospeso', value: 'SUSPENDED' },
        { label: 'Disattivo', value: 'INACTIVE' },
      ],
    },
    { title: 'Gerarchia', field: 'hierarchyDisplay', filterType: 'none' },
    { title: 'Riferimento sede/territorio', field: 'locationDisplay', filterType: 'none' },
    { title: 'Azioni', type: 'action', filterType: 'none' },
  ];

  /** Righe piatte per la registry-table A. */
  tableARows = computed<SolRowView[]>(() => {
    const siteById = new Map(this.sites().map((s) => [s.id, s]));
    return this.rows().map((r) => {
      const site = siteById.get(r.siteId);
      // Indirizzo sede decodificato (tipo via ecc.); fallback alla stringa grezza del BE se sede assente.
      const decodedAddress = site ? this.formatSiteAddress(site) : r.siteAddress || '';
      const siteAddressDisplay = decodedAddress
        ? decodedAddress
        : r.legalFallback
          ? 'Coincide con la sede legale'
          : '—';
      return {
        ...r,
        codeDisplay:
          r.kind === 'pending'
            ? 'Da inserire'
            : r.sol!.master
              ? `${r.sol!.code} (master)`
              : r.sol!.code,
        denominationDisplay: r.kind === 'pending' ? '—' : r.sol!.denomination?.trim() || '—',
        categoryDisplay: r.kind === 'pending' ? '—' : r.sol!.category || '—',
        solStatus: r.kind === 'pending' ? '' : r.sol!.status,
        hierarchyDisplay:
          r.kind === 'pending' ? '—' : this.hierarchyLabel(r.sol!.hierarchyParentCode, r.sol!.hierarchySuffix),
        locationDisplay:
          r.kind === 'pending'
            ? siteAddressDisplay
            : r.sol!.physicalSite
              ? siteAddressDisplay
              : `Territorio: ${r.sol!.region ?? '—'} (${r.sol!.province ?? '—'}) — Sede associata: ${siteAddressDisplay}`,
      };
    });
  });

  /** "Gerarchia" = Codice SOL di gerarchia e Suffisso concatenati con uno spazio. */
  hierarchyLabel(code: string | null | undefined, suffix: string | null | undefined): string {
    return [code, suffix].filter((v) => !!v && v.trim()).join(' ').trim() || '—';
  }

  /**
   * Un SOL è "compilato" quando ha mail principale + responsabile (nome e cognome).
   * Rispecchia il controllo hard lato BE (SolCodeServiceImpl.isSolCompiled).
   */
  private isSolCompiled(sol: SolCode): boolean {
    return (
      !!sol.principalEmail?.trim() &&
      !!sol.principalHolderFirstName?.trim() &&
      !!sol.principalHolderLastName?.trim()
    );
  }

  /**
   * Evidenzia in giallo il master finché non è completato correttamente:
   * - se il master non esiste ancora → le righe "Da inserire" (dove va creato il master);
   * - se il master esiste ma non ha i dati MFA (mail principale + responsabile) → la sua riga.
   */
  readonly rowWarningClass = (row: SolRowView): string => {
    if (row.kind === 'sol' && row.sol?.master && !this.isSolCompiled(row.sol)) {
      return 'row-warning';
    }
    if (row.kind === 'pending' && this.missingMaster()) {
      return 'row-warning';
    }
    return '';
  };

  /** True se la riga è il codice SOL master: una volta creato non è più eliminabile (si modifica, non si elimina). */
  protectedMaster(row: SolRowView): boolean {
    return row.kind === 'sol' && !!row.sol?.master;
  }

  /**
   * Un codice SOL è "attivato" (attivo o oltre) quando il suo stato è diverso da TO_ACTIVATE.
   * Da questo momento non è più eliminabile e ogni modifica ai dati è irreversibile: va
   * confermata esplicitamente sia all'apertura della modifica sia al salvataggio.
   */
  isActivatedSol(sol: SolCode | null | undefined): boolean {
    return !!sol && sol.status !== 'TO_ACTIVATE';
  }

  /** True se la riga SOL non è eliminabile: master oppure già attivato. */
  deleteDisabled(row: SolRowView): boolean {
    return this.protectedMaster(row) || (row.kind === 'sol' && this.isActivatedSol(row.sol));
  }

  /** Tooltip del pulsante elimina: motivo del blocco oppure "Elimina" se consentito. */
  deleteTooltip(row: SolRowView): string {
    if (this.protectedMaster(row)) return 'Il codice SOL master non è eliminabile';
    if (row.kind === 'sol' && this.isActivatedSol(row.sol)) {
      return 'Un codice SOL attivo non è eliminabile';
    }
    return 'Elimina';
  }

  // ── Gating modifica (fase di validazione MFA) ────────────────────────────────
  /**
   * Azioni strutturali (Aggiungi/Modifica/Elimina) attualmente consentite:
   * - fuori dalla fase MFA: sempre (se il BO può gestire);
   * - in fase di validazione MFA: solo dopo lo sblocco esplicito ("Modifica").
   */
  canEditNow = computed<boolean>(() => this.canManage() && (!this.mfaReviewMode() || this.editMode()));

  /** In validazione MFA con modifica non ancora sbloccata: le azioni sono bloccate. */
  editLocked = computed<boolean>(() => this.mfaReviewMode() && !this.editMode());

  /**
   * L'aggiunta di un nuovo codice SOL è bloccata quando la modifica MFA non è sbloccata
   * oppure quando il fiduciario è in fase contrattuale (torna disponibile dopo l'attivazione).
   */
  addBlocked = computed<boolean>(() => this.editLocked() || this.contractPhase());

  /** Tooltip/messaggio del blocco aggiunta, in base alla causa. */
  addBlockedTooltip = computed<string>(() => {
    if (this.contractPhase()) {
      return 'Non è possibile aggiungere codici SOL durante la fase contrattuale: ' +
        'l\'aggiunta sarà disponibile dopo l\'attivazione del fiduciario.';
    }
    if (this.editLocked()) {
      return 'Clicca prima su Modifica per abilitare l\'aggiunta';
    }
    return '';
  });

  /** Associazioni (impiegati amministrativi/collaboratori) del SOL aperto nel dettaglio. */
  selectedAssociations = computed<MfaAssociation[]>(() => {
    const sol = this.selectedSol();
    if (!sol) return [];
    return this.mfaData().find((m) => m.solCodeId === sol.id)?.associations ?? [];
  });

  /** Etichetta leggibile del tipo persona associata. */
  associationKindLabel(assoc: MfaAssociation): string {
    return assoc.kind === 'EMPLOYEE' ? 'Impiegato amministrativo' : 'Collaboratore';
  }

  /**
   * Ruolo da mostrare accanto al tipo persona: solo per i collaboratori e solo se aggiunge
   * informazione. Per gli impiegati amministrativi il ruolo grezzo ("impiegato_amministrativo")
   * è ridondante col tipo, quindi non va mostrato (coerente con la vista lato fiduciario).
   */
  associationRole(assoc: MfaAssociation): string | null {
    return assoc.kind === 'EMPLOYEE' ? null : assoc.role;
  }

  /**
   * Sblocca la modifica in fase di validazione MFA previa conferma: qualsiasi modifica
   * (aggiunta/modifica/eliminazione) è irreversibile e genera una nuova richiesta di
   * compilazione MFA al fiduciario, disabilitando l'approvazione.
   */
  requestEditMode(): void {
    this.confirmationDialog.confirm({
      header: 'Sblocca modifica',
      message:
        'Sbloccando la modifica potrai aggiungere, modificare o eliminare i codici SOL. ' +
        'Attenzione: ogni modifica è irreversibile e comporterà una nuova richiesta di ' +
        'compilazione dei dati MFA al fiduciario, disabilitando l\'approvazione. Continuare?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sblocca modifica',
      rejectLabel: 'Annulla',
      accept: () => this.editMode.set(true),
    });
  }

  /** Click su "Aggiungi codice SOL": bloccato in fase contrattuale e in fase MFA non sbloccata. */
  onAddClick(): void {
    if (this.contractPhase()) {
      this.messageService.add({
        severity: 'info',
        summary: 'Aggiunta non disponibile',
        detail:
          'Non è possibile aggiungere codici SOL durante la fase contrattuale. ' +
          'L\'aggiunta sarà disponibile dopo l\'attivazione del fiduciario.',
        life: 5000,
      });
      return;
    }
    if (this.editLocked()) {
      this.messageService.add({
        severity: 'info',
        summary: 'Modifica bloccata',
        detail: 'Clicca prima su "Modifica" per abilitare l\'aggiunta di un codice SOL.',
        life: 4000,
      });
      return;
    }
    this.openAdd();
  }

  constructor() {
    // Le regioni non dipendono dal fiduciario: si caricano una sola volta (paese IT).
    this.geographyService.getRegions('IT').subscribe({
      next: (res) => this.regions.set(res ?? []),
      error: () => this.regions.set([]),
    });

    // Dizionari di dominio (indipendenti dal fiduciario): tipo via e tipo società.
    this.loadDropdownMap('PLACE_NAME', 'PLACE_NAME', this.streetTypeMap);
    this.loadDropdownMap('LEGAL_PERSON', 'LEGAL_PERSON', this.companyTypeMap);

    effect(() => {
      const id = this.professionalId();
      if (id) {
        this.loadAll(id);
        this.loadCompanyData(id);
      }
    });

    // In fase contrattuale i codici SOL sono solo consultabili: la sezione parte collassata
    // (l'utente può comunque riaprirla). Applicato una sola volta all'ingresso in fase contratto.
    effect(() => {
      if (this.contractPhase() && !this.autoCollapseApplied) {
        this.autoCollapseApplied = true;
        this.collapsed.set(true);
      }
    });
  }

  /** Evita di ri-collassare la sezione dopo che l'utente l'ha riaperta manualmente. */
  private autoCollapseApplied = false;

  /** Carica un dominio dropdown come mappa codice → descrizione. */
  private loadDropdownMap(formId: string, fieldId: string, target: typeof this.streetTypeMap): void {
    this.formDropdownService.getFormDropdownValues(formId, fieldId).subscribe({
      next: (res) => {
        const map = new Map<string, string>();
        for (const item of res?.data ?? []) {
          map.set(item.valueKey, item.valueDescription);
        }
        target.set(map);
      },
      error: () => target.set(new Map()),
    });
  }

  /** Recupera i dati anagrafici (ragione sociale, tipo società, ecc.) per la denominazione. */
  private loadCompanyData(professionalId: string): void {
    this.professionalsService.getProfessionalGeneralData(professionalId).subscribe({
      next: (res) => {
        const data = res?.data ?? {};
        const cd = data.companyData ?? data ?? {};
        // Il nominativo corrente è quello del rappresentante legale; la registrazione resta il fallback.
        const rep = data.legalRepresentative ?? {};
        this.companyData.set({
          businessName: cd.businessName ?? null,
          firstname: rep.firstname || cd.firstname || null,
          lastname: rep.lastname || cd.lastname || null,
          legalOrPhysical: cd.legalOrPhysical ?? null,
          companyType: cd.companyType ?? null,
        });
      },
      error: () => this.companyData.set(null),
    });
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  /**
   * Ricarica dal BE codici SOL, sedi e dati anagrafici. Esposto al contenitore per
   * forzare il refresh dopo l'approvazione anagrafica: nei casi di primo impianto i
   * codici SOL possono già esistere e vanno recuperati (l'auto-load alla creazione del
   * componente non è garantito quando la sezione viene montata dal cambio di step).
   */
  reload(): void {
    const id = this.professionalId();
    if (id) {
      this.loadAll(id);
      this.loadCompanyData(id);
    }
  }

  private loadAll(professionalId: string): void {
    this.loadSol(professionalId);
    this.solCodeService.getSites(professionalId).subscribe({
      next: (res) => {
        const sites = res.data ?? [];
        this.sites.set(sites);
        this.loadMunicipalityNames(sites);
      },
      error: () => this.sites.set([]),
    });
  }

  /**
   * Popola la mappa comune (codice → nome) per le sedi caricate: i comuni si
   * caricano per provincia, quindi richiediamo solo le sigle provincia presenti
   * tra le sedi. Il codice comune è nazionalmente univoco: una mappa piatta basta.
   */
  private loadMunicipalityNames(sites: OperationalSite[]): void {
    const provinceCodes = new Set(
      sites.map((s) => s.province?.trim()).filter((p): p is string => !!p),
    );
    if (provinceCodes.size === 0) return;
    forkJoin(
      [...provinceCodes].map((pc) => this.geographyService.getMunicipalitiesCached(pc)),
    ).subscribe({
      next: (results) => {
        const map = new Map(this.municipalityMap());
        for (const municipalities of results) {
          for (const m of municipalities ?? []) map.set(m.code, m.name);
        }
        this.municipalityMap.set(map);
      },
      error: () => {
        /* Fallback silenzioso: formatSiteAddress mostra il codice grezzo. */
      },
    });
  }

  private loadSol(professionalId: string): void {
    this.loadingSol.set(true);
    this.solCodeService.getSolCodes(professionalId).subscribe({
      next: (res) => {
        this.solCodes.set(res.data ?? []);
        this.loadingSol.set(false);
      },
      error: () => {
        this.solCodes.set([]);
        this.loadingSol.set(false);
      },
    });
  }

  // ── Geografia (cascata Regione → Provincia) ──────────────────────────────────
  /** Cambio Regione dalla tendina: resetta la Provincia e ricarica l'elenco province. */
  onRegionChange(regionName: string | null): void {
    this.form.update((f) => ({ ...f, region: regionName ?? '', province: '' }));
    this.loadProvincesForRegionName(regionName);
  }

  /** Carica le province della regione (per nome) per popolare la tendina dipendente. */
  private loadProvincesForRegionName(regionName: string | null | undefined): void {
    const code = this.regions().find((r) => r.name === regionName)?.code;
    if (!code) {
      this.provinces.set([]);
      return;
    }
    this.loadingProvinces.set(true);
    this.geographyService.getProvinces(code).subscribe({
      next: (res) => {
        this.provinces.set(res ?? []);
        this.loadingProvinces.set(false);
      },
      error: () => {
        this.provinces.set([]);
        this.loadingProvinces.set(false);
      },
    });
  }

  // ── Dialog form ──────────────────────────────────────────────────────────────
  openAdd(): void {
    this.editingId.set(null);
    this.targetSiteId.set(null);
    const isMaster = !this.siteHasMaster();
    this.form.set({
      ...this.emptyForm(),
      denomination: this.suggestedDenomination(),
      category: CATEGORY_BY_TYPE[this.professionalType() as SolProfessionalType] ?? '',
      isMaster,
      hierarchySameAsCode: isMaster,
      hierarchyParentCode: isMaster ? '' : this.siteMasterCode(),
    });
    this.provinces.set([]);
    this.masterLocked.set(true);
    this.formSubmitted.set(false);
    this.serverErrors.set({});
    this.showFormDialog.set(true);
  }

  openEdit(sol: SolCode): void {
    // Su un codice SOL attivo la modifica è irreversibile: richiedi conferma prima di aprire il form.
    if (this.isActivatedSol(sol)) {
      this.confirmationDialog.confirm({
        header: 'Modifica codice SOL attivo',
        message:
          `Il codice SOL "${sol.code}" è in stato ${this.statusLabel(sol.status)}. ` +
          'Ogni modifica ai dati è irreversibile. Vuoi procedere con la modifica?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Procedi con la modifica',
        rejectLabel: 'Annulla',
        accept: () => this.applyEditForm(sol),
      });
      return;
    }
    this.applyEditForm(sol);
  }

  private applyEditForm(sol: SolCode): void {
    this.editingId.set(sol.id);
    this.targetSiteId.set(sol.siteId);
    this.form.set({
      code: sol.code ?? '',
      denomination: sol.denomination ?? '',
      category: sol.category ?? '',
      isMaster: sol.master,
      hierarchySameAsCode: sol.hierarchySameAsCode,
      hierarchyParentCode: sol.hierarchyParentCode ?? '',
      hierarchySuffix: sol.hierarchySuffix ?? null,
      description: sol.description ?? '',
      physicalSite: sol.physicalSite,
      region: sol.region ?? '',
      province: sol.province ?? '',
    });
    // SOL su territorio: precarica le province della regione salvata per popolare la tendina.
    if (!sol.physicalSite && sol.region) {
      this.loadProvincesForRegionName(sol.region);
    } else {
      this.provinces.set([]);
    }
    this.masterLocked.set(true);
    this.formSubmitted.set(false);
    this.serverErrors.set({});
    this.showDetailDialog.set(false);
    this.showFormDialog.set(true);
  }

  save(): void {
    this.formSubmitted.set(true);
    if (!this.isFormValid()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Campi obbligatori',
        detail: 'Compila tutti i campi obbligatori evidenziati.',
      });
      return;
    }

    const f = this.form();
    const sameAsCode = f.isMaster || f.hierarchySameAsCode;
    const payload: SolCodeUpsert = {
      siteId: this.targetSiteId(),
      code: f.code.trim(),
      denomination: f.denomination?.trim() || null,
      category: f.category?.trim() ?? '',
      description: f.description ?? '',
      physicalSite: f.physicalSite,
      region: f.physicalSite ? null : (f.region?.trim() || null),
      province: f.physicalSite ? null : (f.province?.trim() || null),
      master: f.isMaster,
      hierarchySameAsCode: sameAsCode,
      hierarchyParentCode: sameAsCode
        ? f.code.trim() || null
        : f.hierarchyParentCode?.trim() || null,
      hierarchySuffix: this.showSuffix() ? f.hierarchySuffix : null,
    };

    // Aggiornamento di un codice SOL attivo: la modifica è irreversibile, conferma prima di salvare.
    const editingId = this.editingId();
    const editingSol =
      editingId !== null ? this.solCodes().find((s) => s.id === editingId) : undefined;
    if (this.isActivatedSol(editingSol)) {
      this.confirmationDialog.confirm({
        header: 'Conferma modifica codice SOL attivo',
        message:
          `Stai aggiornando il codice SOL "${editingSol!.code}" in stato ${this.statusLabel(editingSol!.status)}. ` +
          'La modifica è irreversibile. Confermi il salvataggio?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Salva modifiche',
        rejectLabel: 'Annulla',
        accept: () => this.performSave(payload),
      });
      return;
    }
    this.performSave(payload);
  }

  private performSave(payload: SolCodeUpsert): void {
    this.saving.set(true);
    const id = this.professionalId();
    const editingId = this.editingId();
    const request$ =
      editingId === null
        ? this.solCodeService.createSolCode(id, payload)
        : this.solCodeService.updateSolCode(id, editingId, payload);


    request$.subscribe({
      next: () => {
        this.saving.set(false);
        this.showFormDialog.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Salvato',
          detail: editingId === null ? 'Codice SOL creato.' : 'Codice SOL aggiornato.',
        });
        this.loadAll(id);
        // In validazione MFA la modifica strutturale invalida la compilazione: notifica il contenitore.
        if (this.mfaReviewMode()) {
          this.solCodesChanged.emit();
        }
      },
      error: (err) => {
        this.saving.set(false);
        const fieldErrors = err?.error?.errors as Record<string, string> | undefined;
        if (fieldErrors?.['code']) {
          this.serverErrors.set({ code: fieldErrors['code'] });
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: err?.error?.message ?? 'Impossibile salvare il codice SOL.',
        });
      },
    });
  }

  confirmDelete(sol: SolCode): void {
    // Un codice SOL attivo (o oltre) non è eliminabile: blocca e informa.
    if (this.isActivatedSol(sol)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Eliminazione non consentita',
        detail: `Il codice SOL "${sol.code}" è attivo e non può essere eliminato.`,
        life: 4000,
      });
      return;
    }
    // In validazione MFA l'eliminazione è irreversibile e genera una nuova richiesta MFA.
    const message = this.mfaReviewMode()
      ? `Eliminare il codice SOL "${sol.code}"? La modifica è irreversibile e comporterà una ` +
        'nuova richiesta di compilazione dei dati MFA al fiduciario, disabilitando l\'approvazione.'
      : `Eliminare il codice SOL "${sol.code}"?`;
    this.confirmationDialog.confirmDelete(
      message,
      () => {
        const id = this.professionalId();
        this.solCodeService.deleteSolCode(id, sol.id).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Eliminato', detail: 'Codice SOL eliminato.' });
            this.loadAll(id);
            if (this.mfaReviewMode()) {
              this.solCodesChanged.emit();
            }
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: err?.error?.message ?? 'Impossibile eliminare il codice SOL.',
            });
          },
        });
      },
      'Conferma eliminazione',
    );
  }

  // ── Cambio stato manuale (Back Office) ───────────────────────────────────────
  /** Il SOL attivo può essere sospeso. */
  canSuspend(sol: SolCode | null): boolean {
    return this.canManage() && sol?.status === 'ACTIVE';
  }

  /** Il SOL sospeso può essere riattivato. */
  canReactivate(sol: SolCode | null): boolean {
    return this.canManage() && sol?.status === 'SUSPENDED';
  }

  /** Il SOL attivo o sospeso può essere disattivato (stato finale, non riattivabile). */
  canDeactivate(sol: SolCode | null): boolean {
    return this.canManage() && (sol?.status === 'ACTIVE' || sol?.status === 'SUSPENDED');
  }

  suspendSol(sol: SolCode): void {
    this.changeStatus(
      sol,
      'SUSPENDED',
      'Sospendi codice SOL',
      `Vuoi sospendere il codice SOL "${sol.code}"? Potrà essere riattivato in seguito.`,
    );
  }

  reactivateSol(sol: SolCode): void {
    this.changeStatus(
      sol,
      'ACTIVE',
      'Riattiva codice SOL',
      `Vuoi riattivare il codice SOL "${sol.code}"?`,
    );
  }

  deactivateSol(sol: SolCode): void {
    this.changeStatus(
      sol,
      'INACTIVE',
      'Disattiva codice SOL',
      `Vuoi disattivare il codice SOL "${sol.code}"? L'operazione è irreversibile: ` +
        'un codice SOL disattivo non può più essere riattivato.',
      true,
    );
  }

  /** Chiede conferma e applica il cambio di stato tramite l'endpoint dedicato. */
  private changeStatus(
    sol: SolCode,
    target: SolCodeStatus,
    header: string,
    message: string,
    danger = false,
  ): void {
    const apply = () => {
      const id = this.professionalId();
      this.solCodeService.changeSolCodeStatus(id, sol.id, target).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Stato aggiornato',
            detail: `Codice SOL ${sol.code}: stato aggiornato a ${this.statusLabel(target)}.`,
          });
          this.showDetailDialog.set(false);
          this.loadAll(id);
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: err?.error?.message ?? 'Impossibile aggiornare lo stato del codice SOL.',
          });
        },
      });
    };
    if (danger) {
      this.confirmationDialog.confirmDelete(message, apply, header);
    } else {
      this.confirmationDialog.confirm({
        header,
        message,
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Conferma',
        rejectLabel: 'Annulla',
        accept: apply,
      });
    }
  }

  // ── Dialog dettaglio ─────────────────────────────────────────────────────────
  openDetail(sol: SolCode): void {
    this.selectedSol.set(sol);
    this.showDetailDialog.set(true);
  }

  /**
   * Indirizzo della sede fisica del SOL con il tipo via decodificato (come in tabella):
   * risolve la sede tramite il siteId e usa {@link formatSiteAddress}; se la sede non è
   * disponibile ripiega sulla stringa grezza del BE (che potrebbe non essere decodificata).
   */
  solSiteAddress(sol: SolCode): string {
    const site = this.sites().find((s) => s.id === sol.siteId);
    if (site) return this.formatSiteAddress(site);
    return sol.siteAddress || '—';
  }

  // ── Util ─────────────────────────────────────────────────────────────────────
  onCodeInput(value: string): void {
    const code = (value ?? '').toUpperCase();
    if (this.serverErrors().code) this.serverErrors.update((e) => ({ ...e, code: undefined }));
    // La gerarchia del master coincide sempre col proprio codice: mantieni allineato il campo.
    this.form.update((f) => ({ ...f, code, hierarchyParentCode: f.isMaster ? code : f.hierarchyParentCode }));
  }

  /** Cambia il flag master: il master coincide sempre con il proprio codice (gerarchia bloccata). */
  onMasterChange(isMaster: boolean): void {
    this.form.update((f) => ({
      ...f,
      isMaster,
      hierarchySameAsCode: isMaster ? true : f.hierarchySameAsCode,
      hierarchyParentCode: isMaster ? f.code : f.hierarchyParentCode,
    }));
  }

  /**
   * Cambio "SOL su sede fisica": anche i SOL su territorio devono avere una sede fisica
   * associata (link figurativo per l'estrazione). Passando a territorio si precompila con la
   * sede legale se non già valorizzata; tornando su sede fisica la scelta resta invariata.
   */
  onPhysicalSiteChange(physicalSite: boolean): void {
    this.form.update((f) => ({ ...f, physicalSite }));
    if (!physicalSite && this.targetSiteId() == null) {
      this.targetSiteId.set(this.legalSiteId());
    }
  }

  updateForm<K extends keyof SolForm>(key: K, value: SolForm[K]): void {
    this.form.update((f) => ({ ...f, [key]: value }));
  }

  /** True se il fiduciario ha già un codice SOL master (master unico per fiduciario). */
  private siteHasMaster(): boolean {
    return this.solCodes().some((c) => c.master);
  }

  /** Codice del master del fiduciario; '' se assente. */
  private siteMasterCode(): string {
    return this.solCodes().find((c) => c.master)?.code ?? '';
  }

  private emptyForm(): SolForm {
    return {
      code: '',
      denomination: '',
      category: '',
      isMaster: false,
      hierarchySameAsCode: false,
      hierarchyParentCode: '',
      hierarchySuffix: null,
      description: '',
      physicalSite: true,
      region: '',
      province: '',
    };
  }
}
